import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const textColor = scrolled || !isHome
    ? 'text-foreground-900'
    : 'text-background-50';
  const subColor = scrolled || !isHome
    ? 'text-foreground-600'
    : 'text-background-50/70';
  const borderColor = scrolled || !isHome
    ? 'border-secondary-300'
    : 'border-background-50/30';
  const hoverBg = scrolled || !isHome
    ? 'hover:bg-background-100'
    : 'hover:bg-background-50/10';
  const logoColor = scrolled || !isHome
    ? 'text-foreground-950'
    : 'text-background-50';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/98 backdrop-blur-lg border-b border-secondary-200'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16">
          <a
            href="/"
            className={`font-heading text-xl md:text-2xl font-bold whitespace-nowrap transition-colors duration-300 ${logoColor}`}
          >
            CSSBuy<span className="text-primary-400">.</span>watch
          </a>

          <div className="hidden md:flex items-center gap-5">
            <button
              onClick={() => navigate('/')}
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${subColor} ${hoverBg} px-3 py-1.5 rounded-full`}
            >
              Início
            </button>

            <button
              onClick={() => navigate('/artigo/guia-de-compras-cssbuy')}
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${subColor} ${hoverBg} px-3 py-1.5 rounded-full`}
            >
              Guia de Compras
            </button>

            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${borderColor} ${subColor}`}
              >
                <span>{t('dropdown_placeholder')}</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`ri-arrow-down-s-line transition-transform duration-200 ${
                      dropdownOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              {dropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setDropdownOpen(false)}
                  ></div>
                  <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-secondary-200 shadow-lg z-20 py-2 max-h-96 overflow-y-auto">
                    <p className="px-4 py-2 text-xs text-foreground-400 font-medium uppercase tracking-wider border-b border-secondary-100 mb-1">
                      Todas as Categorias
                    </p>
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      >
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 flex-shrink-0">
                          <i className={`${getCategoryIcon(cat.id)} text-secondary-500 text-xs`}></i>
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium block truncate">{cat.name}</span>
                          <span className="text-foreground-400 text-xs">{cat.description}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`ml-1 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                scrolled || !isHome
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50 text-foreground-900 hover:bg-background-100'
              }`}
            >
              Acessar
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${textColor}`}
          >
            <i
              className={`text-xl ${
                mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
              }`}
            ></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-secondary-200 px-4 py-3 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/'); }}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-home-line text-primary-500"></i>
              </div>
              Início
            </button>

            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/artigo/guia-de-compras-cssbuy'); }}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-book-open-line text-primary-500"></i>
              </div>
              Guia de Compras
            </button>

            <div className="border-t border-secondary-100 pt-1 mt-1">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-grid-line text-primary-500"></i>
                  </div>
                  {t('dropdown_placeholder')}
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''} text-foreground-400`}></i>
                </div>
              </button>

              {mobileCatOpen && (
                <div className="ml-3 mt-1 border-l-2 border-primary-100 pl-3 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-primary-50 hover:text-primary-700 transition-colors"
                    >
                      <div className="w-5 h-5 flex items-center justify-center rounded bg-secondary-100 flex-shrink-0">
                        <i className={`${getCategoryIcon(cat.id)} text-secondary-500 text-xs`}></i>
                      </div>
                      <span className="font-medium text-xs truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-2 text-center py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold"
            >
              Acessar CSSBuy Sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

function getCategoryIcon(id: string): string {
  const icons: Record<string, string> = {
    shoes: 'ri-footprint-line',
    hoodies: 'ri-t-shirt-line',
    tshirts: 'ri-shirt-line',
    jackets: 'ri-t-shirt-line',
    pants: 'ri-archive-line',
    headwear: 'ri-star-line',
    sets: 'ri-stack-line',
    underwear: 'ri-archive-line',
    jersey: 'ri-team-line',
    accessories: 'ri-glasses-line',
    bags: 'ri-briefcase-line',
    belts: 'ri-circle-line',
    perfumes: 'ri-drop-line',
    other: 'ri-more-line',
  };
  return icons[id] || 'ri-store-line';
}